import React from "react"
import {
  Divider, Flex, Stack, Switch, Text,
} from "@chakra-ui/react"
import { Control, useController, UseFormWatch } from "react-hook-form"
import { useQueryClient } from "react-query"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import Icon from "components/elements/Icon"
import WhatsAppIcon from "components/elements/WhatsAppIcon"
import * as api from "api/guests"
import { Guest } from "sharedTypes"
import { updateGuest } from "context/actions"
import { handleError, successToast } from "utilities/toasts"
import useFeatureFlag from "services/featureFlags"

// TODO(BE): when a guest's `opted_in_whatsapp` flips true → false from any
// path, the BE should also cancel any pending `awaiting_opt_in` GuestText rows
// for that Person (mirroring the webhook NO/STOP handler). Cleanest as an
// after_update_commit callback on Person or a service object the controller
// calls. Without this, admin-revoked guests can still receive a held message
// if they later reply YES to an opt-in template within the 48h TTL.

type Props = {
  guest: Guest
  onClose: () => void
}

type FormValues = {
  unsubscribed: boolean
  optedInWhatsapp: boolean
}

const GuestCommunicationPreferences = ({ guest, onClose }: Props) => {
  const queryClient = useQueryClient()
  const { whatsappMessaging: whatsAppEnabled } = useFeatureFlag()

  const initialValues: FormValues = {
    unsubscribed: guest.personInfo.unsubscribed,
    optedInWhatsapp: guest.personInfo.optedInWhatsapp,
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const { data } = await api.putGuest(guest.eventId, guest.id, {
        personInfo: {
          unsubscribed: values.unsubscribed,
          optedInWhatsapp: values.optedInWhatsapp,
        },
      })
      updateGuest(queryClient, data)
      successToast({ title: "Communication preferences updated" })
      onClose()
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <ModalForm
      title="Guest Communication Preferences"
      size="lg"
      initialValues={initialValues}
      onClose={onClose}
      onSubmit={onSubmit}
      submitText="Save"
      mode="onChange"
      disableSubmitWhenNotDirty
    >
      {({ control, watch }) => (
        <Stack spacing={6}>
          <Stack spacing={3}>
            <Text>
              Control how this guest can be contacted across all your events.
            </Text>
            <Text>
              These preferences apply to future messages and invitations.
              Guests can update them anytime from the Guest Portal and will
              receive notifications when these preferences are changed.
            </Text>
          </Stack>

          <Stack spacing={3}>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex alignItems="center" gap={2}>
                <Icon icon="email-only-large" size={5} color="blue.500" />
                <Text fontSize="lg" fontWeight="semibold">Email</Text>
              </Flex>
              <SubscribedSwitch name="unsubscribed" control={control} />
            </Flex>
            <Text>Receive event emails and invitations.</Text>
            <EmailUnsubscribeWarning
              watch={watch}
              initialUnsubscribed={initialValues.unsubscribed}
            />
          </Stack>

          {whatsAppEnabled && (
            <>
              <Divider />

              <Stack spacing={3}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center" gap={2}>
                    <WhatsAppIcon size={5} color="whatsapp.500" />
                    <Text fontSize="lg" fontWeight="semibold">WhatsApp</Text>
                  </Flex>
                  <WhatsAppOneWaySwitch
                    name="optedInWhatsapp"
                    control={control}
                    canRevoke={initialValues.optedInWhatsapp}
                  />
                </Flex>
                <Text>Receive messages on WhatsApp.</Text>
                {initialValues.optedInWhatsapp ? (
                  <Text color="red.500">
                    If disabled, WhatsApp messaging cannot be re-enabled
                    until the guest completes a new opt-in.
                  </Text>
                ) : (
                  <Text color="red.500">
                    This can only be enabled after the guest completes a
                    WhatsApp opt-in or submits a form with WhatsApp consent.
                  </Text>
                )}
              </Stack>
            </>
          )}
        </Stack>
      )}
    </ModalForm>
  )
}

// Email persists the INVERSE of "subscribed" on `personInfo.unsubscribed`. The
// switch reads as on/off subscribed for the user but writes the inverse to the
// underlying form field.
type SwitchProps = {
  name: keyof FormValues
  control: Control<FormValues>
}

const SubscribedSwitch = ({ name, control }: SwitchProps) => {
  const { field } = useController({ name, control })

  return (
    <Switch
      size="lg"
      aria-label="Email subscribed"
      isChecked={!field.value}
      onChange={(e) => field.onChange(!e.target.checked)}
    />
  )
}

// WhatsApp consent is one-way at the admin UI level: an admin can revoke
// existing consent but cannot grant it (granting must come through a real
// consent path — webhook YES or form checkbox). The switch is disabled when
// the guest is not currently opted in.
type WhatsAppOneWaySwitchProps = SwitchProps & {
  canRevoke: boolean
}

const WhatsAppOneWaySwitch = ({ name, control, canRevoke }: WhatsAppOneWaySwitchProps) => {
  const { field } = useController({ name, control })

  return (
    <Switch
      size="lg"
      aria-label="WhatsApp opted in"
      isChecked={Boolean(field.value)}
      isDisabled={!canRevoke}
      onChange={(e) => field.onChange(e.target.checked)}
    />
  )
}

const EmailUnsubscribeWarning = ({
  watch, initialUnsubscribed,
}: {
  watch: UseFormWatch<FormValues>
  initialUnsubscribed: boolean
}) => {
  const currentUnsubscribed = watch("unsubscribed")

  if (currentUnsubscribed === initialUnsubscribed) return null
  if (!currentUnsubscribed) return null

  return (
    <Text color="red.500">
      This person will stop receiving event emails and invitations.
    </Text>
  )
}

export default GuestCommunicationPreferences
